/**
 * Live metrics for the marketing hero.
 *
 * Goals:
 *   - Deterministic — every visitor sees the same value for the same hour, in any timezone.
 *   - Realistic — daily shares vary (not flat 1/N), hourly progression varies within each day.
 *   - Cumulative — strictly monotonically increasing within a month, resetting on the 1st.
 *   - Capped — May 2026 caps at 72,600,000 verified views. Each subsequent month +10%.
 *   - Spend follows views at $2.50 CPM ⇒ spend = (views / 1000) × 2.50 = views × 0.0025.
 *
 * Reasoning notes:
 *   - We do all math in UTC so a visitor in Tokyo and a visitor in NYC see the same number
 *     at the same wall-clock instant. The displayed month label uses the viewer's local time
 *     so the copy ("May 2026") still feels right.
 *   - Day-share weights are seeded by (year, month) so they're stable for the whole month
 *     but unique per month. They're normalized to sum to 1.
 *   - Hour weights inside each day are seeded by (year, month, day) and normalized — this
 *     means at hour 24 of any day we land exactly on that day's cumulative target.
 *   - We never use `Math.random()` at runtime; everything flows from a seeded mulberry32
 *     PRNG. Result is stable across renders, reloads, and timezones.
 */

const MAY_2026_VIEWS_CAP = 72_600_000;
const CPM_USD = 2.5;
const MONTHLY_GROWTH = 0.1; // +10% MoM compounding from May 2026

/* ----------------------------------------------------------------- */
/* Deterministic PRNG (mulberry32)                                     */
/* ----------------------------------------------------------------- */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFrom(...nums: number[]): number {
  // Hash a small list of integers into a 32-bit seed
  let h = 2166136261 >>> 0;
  for (const n of nums) {
    h = Math.imul(h ^ (n & 0xff), 16777619);
    h = Math.imul(h ^ ((n >>> 8) & 0xff), 16777619);
    h = Math.imul(h ^ ((n >>> 16) & 0xff), 16777619);
    h = Math.imul(h ^ ((n >>> 24) & 0xff), 16777619);
  }
  return h >>> 0;
}

/* ----------------------------------------------------------------- */
/* Monthly views cap (May 2026 = 72.6M, +10% MoM)                      */
/* ----------------------------------------------------------------- */
export function viewsCapForMonth(yearUtc: number, monthUtc0: number): number {
  // monthUtc0 is 0-indexed (Jan = 0, May = 4)
  const monthsSinceMay2026 = (yearUtc - 2026) * 12 + (monthUtc0 - 4);
  const factor = Math.pow(1 + MONTHLY_GROWTH, Math.max(0, monthsSinceMay2026));
  // Pre-May 2026: ramp down 10% per month (so March is lower than April etc.)
  const adjusted = monthsSinceMay2026 < 0
    ? MAY_2026_VIEWS_CAP * Math.pow(1 - MONTHLY_GROWTH, Math.abs(monthsSinceMay2026))
    : MAY_2026_VIEWS_CAP * factor;
  return Math.round(adjusted);
}

/* ----------------------------------------------------------------- */
/* Day-share weights for a given month                                 */
/* Seeded by (year, month). Each day gets a weight in [0.6, 1.4]      */
/* so daily shares vary but stay realistic — no day is double the     */
/* base, no day is below ~half. Normalized to sum to 1.               */
/* ----------------------------------------------------------------- */
function daysInMonth(yearUtc: number, monthUtc0: number): number {
  return new Date(Date.UTC(yearUtc, monthUtc0 + 1, 0)).getUTCDate();
}

function dayWeights(yearUtc: number, monthUtc0: number): number[] {
  const n = daysInMonth(yearUtc, monthUtc0);
  const rand = mulberry32(seedFrom(yearUtc, monthUtc0, 0xa11ce));
  const raw: number[] = [];
  for (let i = 0; i < n; i++) {
    // Weight in [0.55, 1.45] — varied but bounded
    raw.push(0.55 + rand() * 0.9);
  }
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((w) => w / sum);
}

/* ----------------------------------------------------------------- */
/* Hour-share weights inside a single day                              */
/* Seeded by (year, month, day). 24 weights summing to 1.             */
/* ----------------------------------------------------------------- */
function hourWeights(yearUtc: number, monthUtc0: number, dayUtc: number): number[] {
  const rand = mulberry32(seedFrom(yearUtc, monthUtc0, dayUtc, 0xb0b));
  const raw: number[] = [];
  for (let i = 0; i < 24; i++) {
    // Weight in [0.4, 1.6] — hourly is more variable than daily
    raw.push(0.4 + rand() * 1.2);
  }
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((w) => w / sum);
}

/* ----------------------------------------------------------------- */
/* Cumulative views at a specific UTC instant within the month         */
/* ----------------------------------------------------------------- */
export function viewsAt(now: Date): {
  views: number;
  spend: number;
  monthCap: number;
  monthLabel: string;
  dateLabel: string;
} {
  const year = now.getUTCFullYear();
  const month0 = now.getUTCMonth();
  const day = now.getUTCDate(); // 1-indexed
  const hour = now.getUTCHours(); // 0-23

  const cap = viewsCapForMonth(year, month0);
  const dayW = dayWeights(year, month0);
  const hrW = hourWeights(year, month0, day);

  // Sum of completed prior days
  let dayShare = 0;
  for (let d = 1; d < day; d++) dayShare += dayW[d - 1];

  // Within the current day, hours 0..hour-1 are complete.
  // Hour `hour` is partially complete based on how many minutes/seconds have elapsed
  // — but we only update hourly per spec, so we treat the current hour as fully complete.
  let hourShareInDay = 0;
  for (let h = 0; h <= hour; h++) hourShareInDay += hrW[h];
  const partialDay = dayW[day - 1] * hourShareInDay;

  const fraction = Math.min(1, dayShare + partialDay);
  const views = Math.round(cap * fraction);
  const spend = Math.round(views * 0.0025 * 100) / 100; // $2.50 CPM, two decimals

  const monthFmt = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const dateFmt = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  return {
    views,
    spend,
    monthCap: cap,
    monthLabel: monthFmt.format(now),
    dateLabel: dateFmt.format(now),
  };
}

/* ----------------------------------------------------------------- */
/* Sparkline: cumulative views per day across the current month        */
/* ----------------------------------------------------------------- */
export function dailyCumulativeViews(now: Date): number[] {
  const year = now.getUTCFullYear();
  const month0 = now.getUTCMonth();
  const day = now.getUTCDate();
  const cap = viewsCapForMonth(year, month0);
  const dayW = dayWeights(year, month0);

  const out: number[] = [];
  let cum = 0;
  for (let d = 1; d <= day; d++) {
    cum += dayW[d - 1];
    out.push(Math.round(cap * cum));
  }
  return out;
}

/* ----------------------------------------------------------------- */
/* Active campaigns — deterministic per (year, month). Each campaign  */
/* has a brand, a monthly view goal (a slice of the month cap), and   */
/* a current progress derived from the month's overall progress so    */
/* every campaign climbs as the month advances.                        */
/* ----------------------------------------------------------------- */
export interface ActiveCampaign {
  id: string;
  name: string;
  brand: string;
  goal: number;       // monthly view goal
  progress: number;   // current views toward goal
  pct: number;        // 0..1
}

// Synthetic pool used as a fallback only — if the live RPC fails (offline,
// CORS, anon key rotated) the marketing card still renders something
// plausible instead of going blank. The live data path is preferred.
const CAMPAIGN_POOL: Array<{ brand: string; name: string }> = [
  { brand: "Nimbus", name: "Spring Drop '26" },
  { brand: "Halcyon Audio", name: "Studio One Launch" },
  { brand: "Northbound", name: "Trail Series" },
  { brand: "Vela", name: "Lightweight Hero" },
  { brand: "Orbit Coffee", name: "Cold Brew Wave" },
  { brand: "Linden", name: "Everyday Tee" },
  { brand: "Quill", name: "Writers Edition" },
  { brand: "Aster", name: "Glow Routine" },
  { brand: "Kestrel", name: "GR-2 Reveal" },
  { brand: "Maple & Co", name: "Home Refresh" },
  { brand: "Pace", name: "Marathon Build" },
  { brand: "Loom", name: "Knit Capsule" },
];

// String hash used to seed deterministic per-campaign metrics from the
// real campaign UUID. Same campaign → same goal/perf, so the bar is
// stable across renders and reloads even though it's algorithmic.
function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/**
 * Build per-campaign metrics from a list of real campaigns. Each campaign's
 * goal is a slice of the monthly cap, sized by a stable per-id weight.
 * Progress is monthly-progress * per-campaign perf multiplier (clamped),
 * so as the month advances every bar climbs realistically.
 *
 * Used by the marketing landing page to render the Active Campaigns card
 * from real Supabase data — only campaigns visible on the campaigns page
 * (status=active, budget not ended, has spots, community public).
 */
export function activeCampaignsFromList(
  now: Date,
  list: Array<{ id: string; title: string; brand: string }>
): ActiveCampaign[] {
  if (list.length === 0) return [];
  const year = now.getUTCFullYear();
  const month0 = now.getUTCMonth();
  const cap = viewsCapForMonth(year, month0);
  const allocCap = cap * 0.7;

  // Per-campaign deterministic weights from the campaign id.
  const rawShares = list.map((c) => {
    const r = mulberry32(hashStr(c.id))();
    return 0.6 + r * 0.8;
  });
  const sumShares = rawShares.reduce((a, b) => a + b, 0);
  const goals = rawShares.map((s) => Math.round((s / sumShares) * allocCap));

  const monthProgress = (() => {
    const m = viewsAt(now);
    return m.views / Math.max(1, m.monthCap);
  })();

  return list.map((c, i) => {
    // Per-campaign perf is also seeded from id so the same campaign is
    // consistently "ahead" or "behind" pace.
    const perf = 0.7 + mulberry32(hashStr(c.id) ^ 0xfeedbeef)() * 0.45;
    const pct = Math.min(0.999, Math.max(0.04, monthProgress * perf));
    const progress = Math.round(goals[i] * pct);
    return {
      id: c.id,
      name: c.title,
      brand: c.brand,
      goal: goals[i],
      progress,
      pct,
    };
  });
}

/** Synthetic fallback when no real campaigns are available (e.g. RPC fails). */
export function activeCampaigns(now: Date, count = 4): ActiveCampaign[] {
  const year = now.getUTCFullYear();
  const month0 = now.getUTCMonth();

  const rand = mulberry32(seedFrom(year, month0, 0xc1ca));
  const pool = CAMPAIGN_POOL.slice();
  const picked: typeof CAMPAIGN_POOL = [];
  for (let i = 0; i < count && pool.length > 0; i++) {
    const idx = Math.floor(rand() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }

  return activeCampaignsFromList(
    now,
    picked.map((c) => ({
      id: `${year}-${month0}-${c.brand}-${c.name}`,
      title: c.name,
      brand: c.brand,
    }))
  );
}

/* ----------------------------------------------------------------- */
/* Hour-of-next-update — milliseconds until the next UTC hour rolls    */
/* ----------------------------------------------------------------- */
export function msUntilNextHour(now: Date = new Date()): number {
  const next = new Date(now);
  next.setUTCMinutes(0, 0, 0);
  next.setUTCHours(now.getUTCHours() + 1);
  return next.getTime() - now.getTime();
}
